<template>
  <div>
    <div class="d-flex">
      <h1 style="font-size: 1.5rem">{{ $t('schoolDiary.title') }}</h1>
      <v-progress-circular
        v-if="isFetching"
        color="primary"
        class="mx-4"
        indeterminate
      ></v-progress-circular>
      <v-spacer></v-spacer>
      <v-btn large color="primary" variant="flat" to="/school-diary/create">
        <v-icon start>mdi-plus</v-icon>
        {{ $t('schoolDiary.createNew') }}
      </v-btn>
    </div>

    <v-card variant="flat" class="mt-6 pa-2" color="grey-lighten-5">
      <v-row align="center">
        <!-- Feedback #41: empty cols="3" spacer clipped SEARCH button; disabled -->
        <!-- <v-col cols="3"></v-col> -->
        <v-col cols="3">
          <h5>{{ $t('schoolDiary.dateStart') }}</h5>
          <date-picker v-model="from"></date-picker>
        </v-col>
        <v-col cols="3">
          <h5>{{ $t('schoolDiary.dateEnd') }}</h5>
          <date-picker v-model="to"></date-picker>
        </v-col>
        <h5
        class="mt-4 pl-4"
        >
        {{ $t('schoolDiary.unapproved') }}
        </h5>
        <v-col
          cols="1"
          class="mt-10 ml-0 pl-0"
          >
          <v-checkbox
            v-model="check"
            :disabled="isFetching"
            color="primary"
          >
          </v-checkbox>
        </v-col>
        <v-col cols="1">
          <v-btn
            class="mt-4 search-button"
            color="primary"
            variant="flat"
            large
            @click="searchBtn"
            :loading="isFetching"
          >
            {{ $t('common.search') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-table>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">{{ $t('common.date') }}</th>
            <th class="text-right"></th>
            <th class="text-left" style="width: 10%"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in diaries" :key="d.date">
            <td>{{ formatDate(d.date) }}</td>
            <td>
              <div class="d-flex justify-end">
                <v-chip
                  v-if="d.approvedBy.chief && d.approvedBy.chief != -1"
                  color="primary"
                  class="mx-1"
                  label
                >
                  <v-icon start>mdi-check</v-icon>
                  {{ $t('settings.chief') }}
                </v-chip>
                <v-chip
                  v-if="d.approvedBy.director && d.approvedBy.director != -1"
                  color="primary"
                  class="mx-1"
                  label
                >
                  <v-icon start>mdi-check</v-icon>
                  {{ $t('settings.director') }}
                </v-chip>
                <v-chip v-if="d.status === 'draft'" variant="outlined" class="mx-1"
                  >{{ $t('schoolDiary.draft') }}</v-chip
                >
                <v-chip
                  v-if="d.status === 'applied'"
                  color="primary"
                  variant="outlined"
                  class="mx-1"
                  >{{ $t('schoolDiary.applied') }}</v-chip
                >
              </div>
            </td>
            <td class="text-right">
              <v-btn
                color="primary"
                variant="flat"
                :to="`/school-diary/edit/${d.date}`"
                >{{ $t('common.detail') }}</v-btn
              >
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>

    <v-pagination
      class="pt-4"
      v-model="page"
      @input="pageClick"
      :length="Math.ceil(totalItemsCount / limit)"
    >
    </v-pagination>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import DatePicker from "@/components/atoms/DatePicker.vue";
import { schoolDairyModule } from "@/store/dataModules/schoolDiaryModule";
import dayjs from "dayjs";

interface DataType {
  isFetching: boolean;
  page: number;
}

export default defineComponent({
  components: {
    DatePicker,
  },
  watch: {
    page: {
      handler(val) {
        schoolDairyModule.setPage(val);
        this.search();
      },
    },
  },

  computed: {
    diaries() {
      return [...schoolDairyModule.diaries].sort((d1, d2) => {
        const date1 = dayjs(d1.date);
        const date2 = dayjs(d2.date);
        return date2.diff(date1);
      });
    },
    totalItemsCount: function (): number {
      return schoolDairyModule.totalItemsCount;
    },
    from: {
      get() {
        return schoolDairyModule.from;
      },
      set(value: string) {
        schoolDairyModule.setFrom(value);
      },
    },
    to: {
      get() {
        return schoolDairyModule.to;
      },
      set(value: string) {
        schoolDairyModule.setTo(value);
      },
    },
    check: {
      get() {
        return schoolDairyModule.check;
      },
      set(value: boolean) {
        schoolDairyModule.setCheck(value);
      },
    },
    limit: function (): number {
      return schoolDairyModule.limit;
    },
  },

  data: (): DataType => {
    return {
      isFetching: false,
      page: 1,
    };
  },

  methods: {
    async searchBtn() {
      this.page = 1;
      sessionStorage.setItem('schoolDiaryPage', String(1));
      await this.search();
    },
    async search() {
      this.isFetching = true;
      if (
        schoolDairyModule.from != undefined && schoolDairyModule.to != undefined
      ) {
        if (schoolDairyModule.from != undefined && schoolDairyModule.to == "") {
          true
        } else if (schoolDairyModule.from > schoolDairyModule.to) {
          alert(this.$t('schoolDiary.endDateEarlier'))
          this.isFetching = false;
          return
        }
      }
      if (this.from) sessionStorage.setItem('schoolDiaryFrom', this.from); else sessionStorage.setItem('schoolDiaryFrom', '');
      if (this.to) sessionStorage.setItem('schoolDiaryTo', this.to); else sessionStorage.setItem('schoolDiaryTo', '');
      if (this.check) sessionStorage.setItem('schoolDiaryCheck', "true"); else sessionStorage.setItem('schoolDiaryCheck', "false");
      await schoolDairyModule.listSchoolDiaries();
      this.isFetching = false;
    },
    async pageClick(pageNumber) {
      if (pageNumber) sessionStorage.setItem('schoolDiaryPage', String(pageNumber));
    },
    formatDate(date: string): string {
      return date ? dayjs(date).format("DD/MM/YYYY") : "";
    },
  },

  mounted() {
    const schoolDiaryFrom = sessionStorage.getItem('schoolDiaryFrom');
    const schoolDiaryTo = sessionStorage.getItem('schoolDiaryTo');
    const schoolDiaryCheck = sessionStorage.getItem('schoolDiaryCheck');
    const schoolDiaryPage = sessionStorage.getItem('schoolDiaryPage');

    if (schoolDiaryFrom) this.from = schoolDiaryFrom;
    if (schoolDiaryTo) this.to = schoolDiaryTo;
    if (schoolDiaryCheck == "true") this.check = true; else this.check = false;

    this.search();

    if (schoolDiaryPage) this.page = Number(schoolDiaryPage);
  },
});
</script>

<style scoped lang="scss">
.search-button {
  padding-left: 10px !important;
  padding-right: 10px !important;
  min-width: 110px;
}
</style>
